import { CSSProperties, useEffect, useRef } from 'react';

import useBreakpoint from './useBreakpoint';

interface UseGlowEffectOptions {
  color: string;
  lag?: number; // 0 to 1, where 1 is instant and lower values add more lag
}

export const useGlowEffect = ({ color, lag }: UseGlowEffectOptions) => {
  const { isDesktop } = useBreakpoint();
  const effectiveLag = lag ?? 0.05;
  const ref = useRef<HTMLDivElement>(null);
  const currentPos = useRef({ x: 0, y: 0 });
  const targetPos = useRef({ x: 0, y: 0 });
  const animationFrame = useRef<number | null>(null);
  const isFirstMove = useRef(true);

  useEffect(
    () => () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    },
    []
  );

  const animate = () => {
    if (!ref.current) return;

    currentPos.current.x += (targetPos.current.x - currentPos.current.x) * effectiveLag;
    currentPos.current.y += (targetPos.current.y - currentPos.current.y) * effectiveLag;

    ref.current.style.setProperty('--x-pos', `${currentPos.current.x}px`);
    ref.current.style.setProperty('--y-pos', `${currentPos.current.y}px`);

    const deltaX = Math.abs(targetPos.current.x - currentPos.current.x);
    const deltaY = Math.abs(targetPos.current.y - currentPos.current.y);

    if (deltaX > 2 || deltaY > 2) {
      animationFrame.current = requestAnimationFrame(animate);
    }
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();

    targetPos.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    if (isFirstMove.current) {
      currentPos.current = { ...targetPos.current };
      isFirstMove.current = false;
      ref.current.style.setProperty('--glow-opacity', '1');
    }

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }

    animationFrame.current = requestAnimationFrame(animate);
  };

  const onMouseLeave = () => {
    if (ref.current) {
      ref.current.style.setProperty('--glow-opacity', '0');
    }

    isFirstMove.current = true;
  };

  const style = { '--accent-color': color } as CSSProperties;

  if (!isDesktop) {
    return {
      ref: undefined,
      onMouseMove: undefined,
      onMouseLeave: undefined,
      style,
    };
  }

  return { ref, onMouseMove, onMouseLeave, style };
};
